import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography, Link, Modal, ModalDialog, ModalClose, Button } from '@mui/joy';
import DownloadIcon from '@mui/icons-material/Download';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext.jsx';
import StyledTable from '../components/StyledTable';
import { logEvent } from '../../utils/logEventClient.js';

const ClientDocuments = () => {
  const { user } = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [pendingDoc, setPendingDoc] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    const fetchProjectIds = async () => {
      if (!user?.id) return;

      const { data, error } = await supabase
        .from('user_clients')
        .select('project_id')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching user client projects:', error);
        return;
      }

      const projectIds = data.map(entry => entry.project_id);
      setProjects(projectIds);
    };

    fetchProjectIds();
  }, [user]);

  useEffect(() => {
    if (projects.length === 0) return;

    const fetchDocsAndProjects = async () => {
      const { data: docs, error: docError } = await supabase
        .from('client_documents')
        .select('id, name, storage_path, project_id, uploaded_at, tags, notes')
        .in('project_id', projects);

      if (docError) {
        console.error('Error fetching documents:', docError);
        return;
      }

      const { data: projectData, error: projError } = await supabase
        .from('projects')
        .select('id, name')
        .in('id', projects);

      if (projError) {
        console.error('Error fetching projects:', projError);
        return;
      }

      setDocuments(docs || []);
      setProjects(projectData || []);
    };

    fetchDocsAndProjects();
  }, [projects]);

  const grouped = documents.reduce((acc, doc) => {
    const key = doc.project_id || 'no_project';
    if (!acc[key]) acc[key] = [];
    acc[key].push(doc);
    return acc;
  }, {});

  return (
    <Box sx={{ p: 3, backgroundColor: '#f7f9fc', borderRadius: 8, boxShadow: 'sm', mt: 4 }}>
      <Typography level="h4" sx={{ mb: 2, color: 'success.700' }}>
        Documents By Project
      </Typography>
      {Object.entries(grouped).map(([projectId, docs]) => {
        const projectName = projectId === 'no_project'
          ? 'Unassigned Documents'
          : projects.find(p => p.id === projectId)?.name || 'Unnamed Project';

        return (
          <Box key={projectId} sx={{ mb: 4 }}>
            <Typography level="h5" sx={{ mb: 1, color: 'success.700' }}>
              {projectName}
            </Typography>
            <StyledTable
              columns={[
                { key: 'name', label: 'File Name' },
                { key: 'notes', label: 'Notes' },
                { key: 'uploaded_at', label: 'Uploaded Date' },
                { key: 'download', label: 'Download' }
              ]}
              rows={docs.map(doc => ({
                name: doc.name,
                notes: doc.notes || '—',
                uploaded_at: doc.uploaded_at
                  ? new Date(doc.uploaded_at).toLocaleDateString()
                  : '—',
                download: (
                  <Link
                    component="button"
                    onClick={() => {
                      setPendingDoc(doc);
                      setShowDisclaimer(true);
                    }}
                    underline="none"
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <DownloadIcon />
                  </Link>
                )
              }))}
            />
          </Box>
        );
      })}
      <Modal open={showDisclaimer} onClose={() => setShowDisclaimer(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h5">Disclaimer</Typography>
          <Typography sx={{ my: 2 }}>
            [Liability Disclaimer
Electronic data provided herein is presented for informational purposes only.  Florida Land Design & Permitting (FLD&P) provides no warranty either expressed or implied about the reliability, accuracy, completeness, and/or timeliness of these electronic files.  The recipient assumes responsibility and accepts all conditions under which the data is provided.  FLD&P shall not be liable for any misuse and may revise, edit and enhance any electronic data without prior notice and shall assume no responsibility for nondisclosure of any changes. This material shall not be used by any third party without prior authorization.  Files provided are intended to be free of any viruses, nevertheless, FLD&P accepts no responsibility for any contamination or damage as a result of data use.]
          </Typography>
          <Button
            onClick={() => {
              if (pendingDoc) {
                const publicUrl = `https://kqakdlnewshbnuvrzdus.supabase.co/storage/v1/object/public/client-docs/${pendingDoc.storage_path}`;
                logEvent({
                  event_type: 'disclaimer_ack',
                  target: 'v1.0'
                });
                logEvent({
                  event_type: 'doc_download',
                  target: pendingDoc.id,
                  metadata: {
                    filename: pendingDoc.name,
                    project_id: pendingDoc.project_id
                  }
                });
                window.open(publicUrl, '_blank', 'noopener,noreferrer');
              }
              setShowDisclaimer(false);
              setPendingDoc(null);
            }}
          >
            I Accept
          </Button>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default ClientDocuments;
